export type TrainingExecutionMethodFamily = 'default' | 'drop_family' | 'rest_pause' | 'cluster_set' | 'gvt' | 'tabata';

export interface TrainingExecutionMethodDefinition {
  key: string;
  family: TrainingExecutionMethodFamily;
  displayLabel?: string;
  canonicalValue?: string;
  fixedSets?: number;
  fixedRepetitions?: number;
  fixedRest?: number;
  fixedRestLabel?: string;
}

const DEFAULT_METHOD_DEFINITION: TrainingExecutionMethodDefinition = {
  key: '',
  family: 'default',
};

const TRAINING_EXECUTION_METHOD_DEFINITIONS: Record<string, TrainingExecutionMethodDefinition> = {
  drop_set: {
    key: 'drop_set',
    family: 'drop_family',
  },
  strip_set: {
    key: 'strip_set',
    family: 'drop_family',
  },
  piramid: {
    key: 'piramid',
    family: 'drop_family',
    displayLabel: 'Pirâmide',
  },
  progressive_overload: {
    key: 'progressive_overload',
    family: 'drop_family',
    displayLabel: 'Pirâmide',
    canonicalValue: 'piramid',
  },
  descending_repetitions: {
    key: 'descending_repetitions',
    family: 'drop_family',
    displayLabel: 'Pirâmide',
    canonicalValue: 'piramid',
  },
  rest_pause: {
    key: 'rest_pause',
    family: 'rest_pause',
  },
  failure: {
    key: 'failure',
    family: 'rest_pause',
  },
  cluster_set: {
    key: 'cluster_set',
    family: 'cluster_set',
  },
  gvt: {
    key: 'gvt',
    family: 'gvt',
    fixedSets: 10,
    fixedRepetitions: 10,
    fixedRest: 45,
    fixedRestLabel: '30-60 segundos',
  },
  tabata: {
    key: 'tabata',
    family: 'tabata',
    displayLabel: 'Tabata',
  },
};

export const normalizeTrainingExecutionMethodKey = (value?: string | null) => value?.trim().toLowerCase() ?? '';

export const getCanonicalTrainingExecutionMethodValue = (value?: string | null) => {
  const normalizedValue = normalizeTrainingExecutionMethodKey(value);

  if (!normalizedValue) {
    return '';
  }

  return TRAINING_EXECUTION_METHOD_DEFINITIONS[normalizedValue]?.canonicalValue ?? normalizedValue;
};

export const getTrainingExecutionMethodDefinition = (value?: string | null): TrainingExecutionMethodDefinition => {
  const canonicalValue = getCanonicalTrainingExecutionMethodValue(value);

  return TRAINING_EXECUTION_METHOD_DEFINITIONS[canonicalValue] ?? DEFAULT_METHOD_DEFINITION;
};

export const getTrainingExecutionMethodDisplayLabel = (value?: string | null, fallbackLabel = '') => {
  const normalizedValue = normalizeTrainingExecutionMethodKey(value);

  if (!normalizedValue) {
    return fallbackLabel.trim();
  }

  const directLabel = TRAINING_EXECUTION_METHOD_DEFINITIONS[normalizedValue]?.displayLabel;

  if (directLabel) {
    return directLabel;
  }

  const canonicalValue = getCanonicalTrainingExecutionMethodValue(normalizedValue);
  const canonicalLabel = TRAINING_EXECUTION_METHOD_DEFINITIONS[canonicalValue]?.displayLabel;

  return canonicalLabel ?? fallbackLabel.trim();
};

export const isDropFamilyTrainingExecutionMethod = (value?: string | null) => getTrainingExecutionMethodDefinition(value).family === 'drop_family';

export const isPyramidTrainingExecutionMethod = (value?: string | null) => (
  getCanonicalTrainingExecutionMethodValue(value) === 'piramid'
);

export const usesManualLoadPercentage = (value?: string | null) => {
  const canonicalValue = getCanonicalTrainingExecutionMethodValue(value);

  return canonicalValue === 'drop_set' || canonicalValue === 'strip_set';
};

export const usesPrescribedLoadPercentage = (value?: string | null) => !usesManualLoadPercentage(value);

export const isRestPauseTrainingExecutionMethod = (value?: string | null) => getTrainingExecutionMethodDefinition(value).family === 'rest_pause';

export const isClusterSetTrainingExecutionMethod = (value?: string | null) => getTrainingExecutionMethodDefinition(value).family === 'cluster_set';

export const isGvtTrainingExecutionMethod = (value?: string | null) => getTrainingExecutionMethodDefinition(value).family === 'gvt';

export const isTabataTrainingExecutionMethod = (value?: string | null) => getTrainingExecutionMethodDefinition(value).family === 'tabata';

export const usesStructureFieldForTrainingExecutionMethod = (value?: string | null) => {
  const family = getTrainingExecutionMethodDefinition(value).family;

  return family === 'default' || family === 'drop_family' || family === 'tabata';
};